import { convertDateUnitType } from '@/coreDomainsModule/date';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { TranslateExcelBusiness } from '@/coreDomainsModule/translateExcel';
import { UpdateRegisterBusinessRepository } from '@/coreInfrastructureModule/Repositories/UpdateRegisterBusinessRepository';
import {
  PostBusinessInfo,
  UpdateBusinessForInputValuesPostRequest,
  UpdateBusinessForInputValuesResponseResult,
} from '@/coreInfrastructureModule/axios/generated/registerBusinessManage/api';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';

/**
 * エクセル取り込み(在庫表以外の情報取り込み)のビジネスロジック
 */
export class UpdateRegisterBusinessUseCase {
  /**
   * NOTE: エクセル取り込みを弄りたくないので、既存の登録ロジックを流用している
   * @param repository - 取引情報の永続化の責務を負うクラス
   * @param updateBusinesses - ビジネス
   * @param executionDate - 実行日
   */
  public Execute = async (
    repository: UpdateRegisterBusinessRepository,
    updateBusinesses: TranslateExcelBusiness[],
    executionDate: Date
  ): Promise<INetworkResult<UpdateBusinessForInputValuesResponseResult, IError>> => {
    const req: UpdateBusinessForInputValuesPostRequest = {
      updateAggregateBusinessInfo: [],
    };
    const registeredAggregateBusiness = updateBusinesses.filter((business) => {
      // 4つのプロパティしか持っていない場合は集計とみなす
      if (
        business.warehouseNumber != '' &&
        business.package != '' &&
        business.businessNumber != '' &&
        business.supplierNumber != '' &&
        business.customerNumber == '' &&
        business.salesDestinationNumber == ''
      ) {
        return true;
      }
    });

    const registeredNotAggregateBusiness = updateBusinesses.filter((business) => {
      // 6つのプロパティを持っている場合は取引とみなす
      if (
        business.warehouseNumber != '' &&
        business.package != '' &&
        business.businessNumber != '' &&
        business.supplierNumber != '' &&
        business.customerNumber != '' &&
        business.salesDestinationNumber != ''
      ) {
        return true;
      }
    });

    registeredNotAggregateBusiness.forEach((business) => {
      const putItem: PostBusinessInfo = {
        warehouse: business.warehouse,
        warehouseNumber: business.warehouseNumber,
        package: business.package,
        businessNumber: business.businessNumber,
        supplierName: business.supplierName,
        supplierNumber: business.supplierNumber,
        isAggregateBusiness: false,
        businessName: business?.businessName ?? '',
        unit: business.unit ?? '',
        leadTime: business.leadTime?.toString() ?? '',
        leadTimeUnit: convertDateUnitType(business.leadTimeUnit ?? ''),
        inTransitDays: business.inTransitDays ?? 0,
        moq: business.MOQ,
        orderPackage: business.orderPackage?.toString(),
        stdInventoryIndices: business?.stdInventoryIndices ?? {},
        arrivals: [],
        mvpInventory: [],
        executionDate: DateService.formatDateToISOString(executionDate), // iso8601形式に変換
        customerName: business.customerName ?? '',
        customerNumber: business.customerNumber ?? '',
        salesDestinationName: business.salesDestinationName ?? '',
        salesDestinationNumber: business.salesDestinationNumber ?? '',
        salesResults: [],
        customerAnnounce: [],
        customerAnnounceDaily: [],
        forecastForReceiptMemo: business.receiptForecastMemo?.substring(0, 300) ?? '', // NOTE: バリデーションエラーにするのではなく、300文字で切り取って登録する
      };
      business?.salesResults?.forEach((salesResult) => {
        putItem.salesResults.push({
          year: salesResult.year,
          month: salesResult.month,
          amount: salesResult.amount.roundToDecimalPlacesNumber(6),
        });
      });
      business?.customerAnnounce?.forEach((customerAnnounce) => {
        putItem.customerAnnounce.push({
          manageInitiatedYearMonth: customerAnnounce.manageInitiatedYearMonth,
          year: customerAnnounce.year,
          month: customerAnnounce.month,
          amount: customerAnnounce.amount.roundToDecimalPlacesNumber(6),
        });
      });
      business?.customerAnnounceDaily?.forEach((customerAnnounce) => {
        putItem.customerAnnounceDaily?.push({
          manageInitiatedYearMonth: customerAnnounce.manageInitiatedYearMonth,
          year: customerAnnounce.year,
          month: customerAnnounce.month,
          day: customerAnnounce.day,
          amount: customerAnnounce.amount.roundToDecimalPlacesNumber(6),
        });
      });
      req.updateAggregateBusinessInfo.push(putItem);
    });

    registeredAggregateBusiness.forEach((aggregateBusiness) => {
      const putItem: PostBusinessInfo = {
        warehouse: aggregateBusiness.warehouse,
        warehouseNumber: aggregateBusiness.warehouseNumber,
        package: aggregateBusiness.package,
        businessNumber: aggregateBusiness.businessNumber,
        supplierName: aggregateBusiness.supplierName,
        supplierNumber: aggregateBusiness.supplierNumber,
        isAggregateBusiness: true,
        businessName: aggregateBusiness.businessName ?? '',
        unit: aggregateBusiness.unit ?? '',
        leadTime: aggregateBusiness.leadTime?.toString() ?? '',
        leadTimeUnit: convertDateUnitType(aggregateBusiness.leadTimeUnit ?? ''),
        inTransitDays: aggregateBusiness.inTransitDays ?? 0,
        moq: aggregateBusiness.MOQ,
        orderPackage: aggregateBusiness.orderPackage?.toString(),
        stdInventoryIndices: aggregateBusiness?.stdInventoryIndices ?? {},
        arrivals: [],
        mvpInventory: [],
        executionDate: DateService.formatDateToISOString(executionDate), // iso8601形式に変換
        customerName: aggregateBusiness.customerName ?? '',
        customerNumber: aggregateBusiness.customerNumber ?? '',
        salesDestinationName: aggregateBusiness.salesDestinationName ?? '',
        salesDestinationNumber: aggregateBusiness.salesDestinationNumber ?? '',
        salesResults: [],
        customerAnnounce: [],
        customerAnnounceDaily: [],
      };
      aggregateBusiness?.arrivals?.forEach((arrival) => {
        putItem.arrivals.push({
          year: arrival.year,
          month: arrival.month,
          amount: arrival.amount.roundToDecimalPlacesNumber(6),
        });
      });
      aggregateBusiness?.mvpInventory?.forEach((mvpInventory) => {
        putItem.mvpInventory.push({
          year: mvpInventory.year,
          month: mvpInventory.month,
          amount: mvpInventory.amount.roundToDecimalPlacesNumber(6),
        });
      });

      req.updateAggregateBusinessInfo.push(putItem);
    });

    try {
      const result = await repository.put(req);
      switch (result.status.type) {
        case 'success':
          console.log('取引更新件数', result.status.data.updatedBusinessInfoCounts);
          break;
        case 'error':
          throw result.status.error;
        default:
          throw Error('想定外のエラー');
      }

      return {
        status: {
          type: 'success',
          data: result.status.data,
        },
      };
    } catch (error) {
      console.error('error', error); // 基本的にはここには来ない
      return {
        status: {
          type: 'error',
          error: error as IError,
        },
      };
    }
  };
}
